import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

const PaymentSuccessPage = ({ darkMode }) => {
  const location = useLocation();
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    // Extract payment details from URL parameters or state
    const urlParams = new URLSearchParams(location.search);
    const paymentId = urlParams.get('payment_id');
    const orderId = urlParams.get('order_id');
    
    setPaymentDetails({
      paymentId: paymentId || 'Not available',
      orderId: orderId || 'Not available',
      timestamp: new Date().toLocaleString()
    });
  }, [location]);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} flex items-center justify-center p-4`}>
      <div className="max-w-md w-full">
        {/* Success Animation */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto mb-4 relative">
            <svg className="w-full h-full animate-bounce" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="10" fill="url(#successGradient)" />
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-2">Payment Successful! 🎉</h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Your MBBS seat has been secured
          </p>
        </div>

        {/* Payment Details Card */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl border p-6 mb-6`}>
          <h2 className="text-lg font-semibold mb-4">Payment Details</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Amount Paid:</span>
              <span className="font-semibold text-green-600">₹20,000</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Payment ID:</span>
              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {paymentDetails?.paymentId}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Order ID:</span>
              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {paymentDetails?.orderId}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Time:</span>
              <span className="text-sm">{paymentDetails?.timestamp}</span>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className={`${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-2xl border p-6 mb-6`}>
          <h3 className="text-lg font-semibold mb-3 text-blue-600">What's Next?</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">✓</span>
              <span>Confirmation email sent to your registered email address</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">✓</span>
              <span>SMS confirmation sent to your registered mobile number</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">✓</span>
              <span>Our team will contact you within 24 hours for further process</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">✓</span>
              <span>Download official receipt from your student dashboard</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Link
            to="/status-check"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-medium hover:scale-105 transition-transform duration-200 block text-center"
          >
            Check Application Status
          </Link>
          
          <Link
            to="/"
            className={`w-full ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border-gray-700' : 'bg-white hover:bg-gray-50 border-gray-300'} border py-3 px-6 rounded-xl font-medium hover:scale-105 transition-transform duration-200 block text-center`}
          >
            Back to Home
          </Link>
        </div>

        {/* Support Contact */}
        <div className="text-center mt-6">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
            Need help? Contact our support team
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="tel:+916364249519"
              className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"/>
              </svg>
              +91 6364 249 519
            </a>
            <a
              href="https://wa.me/916364249519"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
